/* Unbound Resonance Fit Reviews (storefront).
   <urfr-account> : Review Your Order page. One shared <dialog> form, filled per purchased
                    item from server-rendered data attributes. Client checks are for UX only;
                    the review server re-validates everything and derives product, size,
                    customer and tier itself (nothing here is trusted).
   <urfr-product> : product-page list ("show more") + pauses off-screen tier marks.
   Listeners are bound to an AbortController so Theme Editor re-renders never duplicate them. */
(() => {
  const CM_PER_IN = 2.54;
  const KG_PER_LB = 0.45359237;
  const MAX_TEXT = 1500;
  const MIN_TEXT = 10;
  const MAX_PHOTO = 10 * 1024 * 1024;
  const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  // Same patterns the server enforces (server list is authoritative).
  const LINK_RE = /(https?:\/\/|www\.|<\s*a\s|\]\s*\(|\b[a-z0-9-]{2,}\s*(?:\.|\[\s*\.\s*\]|\(\s*\.\s*\)|\s+dot\s+)\s*(?:com|net|org|io|co|shop|store|me|gg|app|ly|xyz|us|info|biz|link|tv|site|online|page)\b|\b(?:bit\.ly|tinyurl|linktr\.ee|t\.co)\b)/i;
  const PROMO_RE = /(\b(?:dm|message|text|email|e-mail|call|follow|hmu|contact)\s+me\b|\bfollow\s+(?:my|us|me)\b|\b(?:instagram|insta|tiktok|youtube|snapchat|discord|telegram|whatsapp|onlyfans|cashapp|venmo)\b|(?:^|\s)@[a-z0-9_.]{3,}|\b(?:promo|referral|affiliate|discount)\s+code\b|\buse\s+(?:my\s+)?code\b|\b\d{3}[\s.-]?\d{3}[\s.-]?\d{4}\b|[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/i;

  const pauseMarks = (root, signal) => {
    const marks = root.querySelectorAll('.ur-mark');
    if (!marks.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('is-paused', !e.isIntersecting));
    }, { rootMargin: '150px' });
    marks.forEach((m) => io.observe(m));
    signal.addEventListener('abort', () => io.disconnect());
  };

  if (!customElements.get('urfr-product')) {
    customElements.define('urfr-product', class extends HTMLElement {
      connectedCallback() {
        this.ctrl?.abort();
        this.ctrl = new AbortController();
        const { signal } = this.ctrl;
        pauseMarks(this, signal);
        const more = this.querySelector('[data-urfr-more-btn]');
        if (!more) return;
        const step = Number(this.dataset.step) || 8;
        more.addEventListener('click', () => {
          const hidden = [...this.querySelectorAll('[data-urfr-more][hidden]')];
          hidden.slice(0, step).forEach((li, i) => {
            li.hidden = false;
            if (i === 0) li.querySelector('h3')?.setAttribute('tabindex', '-1');
            if (i === 0) li.querySelector('h3')?.focus({ preventScroll: false });
          });
          if (hidden.length <= step) more.hidden = true;
        }, { signal });
      }
      disconnectedCallback() { this.ctrl?.abort(); }
    });
  }

  if (!customElements.get('urfr-account')) {
    customElements.define('urfr-account', class extends HTMLElement {
      connectedCallback() {
        this.ctrl?.abort();
        this.ctrl = new AbortController();
        const { signal } = this.ctrl;
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('[data-urfr-form]');
        if (!this.dialog || !this.form) return;
        pauseMarks(this, signal);

        this.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-urfr-open]');
          if (opener && !opener.disabled) this.open(opener);
        }, { signal });

        let downOnBackdrop = false;
        this.dialog.addEventListener('pointerdown', (e) => { downOnBackdrop = e.target === this.dialog; }, { signal });
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog && downOnBackdrop) this.dialog.close();
          if (e.target.closest('[data-urfr-close]')) this.dialog.close();
        }, { signal });
        this.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('ur-sg-lock');
          this.opener?.focus({ preventScroll: true });
        }, { signal });

        this.form.addEventListener('change', (e) => {
          if (e.target.name === 'height_unit' || e.target.name === 'weight_unit') this.syncUnits();
        }, { signal });
        const text = this.form.elements.review;
        const counter = this.form.querySelector('[data-urfr-count]');
        text.addEventListener('input', () => { counter.textContent = `${text.value.length} / ${MAX_TEXT}`; }, { signal });
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); }, { signal });
      }

      disconnectedCallback() {
        this.ctrl?.abort();
        if (this.dialog?.open) this.dialog.close();
      }

      $(sel) { return this.form.querySelector(sel); }

      open(opener) {
        this.opener = opener;
        const d = opener.dataset;
        const f = this.form;
        f.reset();
        this.clearErrors();
        this.setStatus('');
        f.elements.line_item_id.value = d.lineItem || '';
        f.elements.order_id.value = d.order || '';
        this.$('[data-urfr-title]').textContent = d.title || '';
        this.$('[data-urfr-variant]').textContent = d.variant || '';
        this.$('[data-urfr-size]').textContent = d.size || '';
        const img = this.$('[data-urfr-img]');
        if (d.image) { img.src = d.image; img.hidden = false; } else { img.removeAttribute('src'); img.hidden = true; }

        // Prefill when editing a published review (values come from the approved entry).
        if (d.rating) {
          const r = f.querySelector(`input[name="rating"][value="${Number(d.rating)}"]`);
          if (r) r.checked = true;
        }
        if (d.review) f.elements.review.value = d.review;
        if (d.heightCm) {
          const totalIn = Math.round(Number(d.heightCm) / CM_PER_IN);
          f.elements.height_ft.value = Math.floor(totalIn / 12);
          f.elements.height_in.value = totalIn % 12;
          f.elements.height_cm.value = Math.round(Number(d.heightCm));
        }
        if (d.weightKg) {
          f.elements.weight_value.value = Math.round(Number(d.weightKg) / KG_PER_LB);
        }
        this.$('[data-urfr-count]').textContent = `${f.elements.review.value.length} / ${MAX_TEXT}`;
        this.$('[data-urfr-mode]').textContent = d.rating ? this.dataset.editTitle : this.dataset.newTitle;
        this.syncUnits();
        this.dialog.showModal();
        document.documentElement.classList.add('ur-sg-lock');
      }

      syncUnits() {
        const f = this.form;
        const hUnit = f.elements.height_unit.value;
        this.$('[data-urfr-h-imperial]').hidden = hUnit !== 'ftin';
        this.$('[data-urfr-h-metric]').hidden = hUnit !== 'cm';
        const wUnit = f.elements.weight_unit.value;
        this.$('[data-urfr-w-label]').textContent = wUnit === 'kg' ? 'kg' : 'lb';
      }

      clearErrors() {
        this.form.querySelectorAll('[data-urfr-error]').forEach((el) => { el.textContent = ''; el.hidden = true; });
        this.form.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
      }

      setError(field, message) {
        const box = this.form.querySelector(`[data-urfr-error="${field}"]`);
        if (box) { box.textContent = message; box.hidden = false; }
        this.form.querySelectorAll(`[data-urfr-field="${field}"]`).forEach((el) => el.setAttribute('aria-invalid', 'true'));
      }

      setStatus(message, kind) {
        const s = this.$('[data-urfr-status]');
        s.textContent = message;
        s.dataset.kind = kind || '';
        s.hidden = !message;
      }

      // Returns { errors, data } where data holds the canonical values the server will re-check.
      collect() {
        const f = this.form;
        const errors = {};
        const rating = Number(f.elements.rating.value);
        if (!(rating >= 1 && rating <= 5)) errors.rating = 'Choose a star rating.';

        let heightCm = NaN;
        if (f.elements.height_unit.value === 'cm') {
          heightCm = Number(f.elements.height_cm.value);
        } else {
          const ft = f.elements.height_ft.value.trim();
          const inch = f.elements.height_in.value.trim() || '0';
          if (ft !== '') heightCm = (Number(ft) * 12 + Number(inch)) * CM_PER_IN;
          if (Number(inch) < 0 || Number(inch) > 11) heightCm = NaN;
        }
        if (!(heightCm >= 90 && heightCm <= 250)) errors.height = 'Enter your height.';

        const wRaw = f.elements.weight_value.value.trim();
        let weightKg = NaN;
        if (wRaw !== '') weightKg = f.elements.weight_unit.value === 'kg' ? Number(wRaw) : Number(wRaw) * KG_PER_LB;
        if (!(weightKg >= 25 && weightKg <= 300)) errors.weight = 'Enter your weight.';

        const text = f.elements.review.value.trim();
        if (text.length < MIN_TEXT) errors.review = 'Write a few words about the fit, feel or quality.';
        else if (text.length > MAX_TEXT) errors.review = `Keep your review under ${MAX_TEXT} characters.`;
        else if (LINK_RE.test(text) || PROMO_RE.test(text)) errors.review = 'Links and promotional content are not allowed in reviews.';

        const photo = f.elements.photo.files[0];
        if (photo) {
          if (!PHOTO_TYPES.includes((photo.type || '').toLowerCase()) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(photo.name)) errors.photo = 'Photos must be JPG, PNG, WebP or HEIC.';
          else if (photo.size > MAX_PHOTO) errors.photo = 'Photos must be 10 MB or smaller.';
        }
        if (!f.elements.consent.checked) errors.consent = 'Please confirm you understand what will be shown publicly.';

        // Public nickname: quick checks only; the server applies the full rules.
        const nick = f.elements.display_name;
        if (nick) {
          const n = nick.value.trim();
          if ([...n].length < 2 || [...n].length > 30) errors.display_name = 'Choose a public display name (2 to 30 characters).';
          else if (LINK_RE.test(n) || PROMO_RE.test(n)) errors.display_name = 'Display names can\'t contain links, contact details or promotions.';
        }

        return { errors, heightCm, weightKg };
      }

      async submit() {
        this.clearErrors();
        this.setStatus('');
        const { errors, heightCm, weightKg } = this.collect();
        const keys = Object.keys(errors);
        if (keys.length) {
          keys.forEach((k) => this.setError(k, errors[k]));
          this.form.querySelector(`[data-urfr-field="${keys[0]}"]`)?.focus();
          return;
        }
        if (this.dataset.enabled !== 'true') {
          this.setStatus(this.dataset.closedMessage || 'Review submissions open soon.', 'info');
          return;
        }
        const body = new FormData(this.form);
        body.set('height_cm', heightCm.toFixed(1));
        body.set('weight_kg', weightKg.toFixed(1));
        const btn = this.$('[type="submit"]');
        btn.disabled = true;
        this.setStatus('Sending…', 'info');
        try {
          const res = await fetch(this.dataset.endpoint, { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' } });
          let json = {};
          try { json = await res.json(); } catch (_) { json = {}; }
          if (res.ok && json.ok) {
            this.setStatus('Thanks. Your review has been submitted and will appear once it has been approved.', 'success');
            const row = this.opener?.closest('[data-urfr-item]');
            const state = row?.querySelector('[data-urfr-state]');
            if (state) state.textContent = 'Submitted — pending approval';
            if (this.opener) { this.opener.hidden = true; }
            // Keep the (possibly new) nickname for the next item; form.reset() restores defaultValue.
            const nick = this.form.elements.display_name;
            if (nick) nick.defaultValue = nick.value.trim();
            setTimeout(() => { if (this.dialog.open) this.dialog.close(); }, 1800);
            this.opener = row?.querySelector('a, button:not([hidden])') || null;
          } else {
            if (json.errors && typeof json.errors === 'object') {
              Object.entries(json.errors).forEach(([k, v]) => this.setError(k, String(v)));
            }
            const msg = res.status === 401 ? 'Please sign in again and retry.'
              : res.status === 429 ? 'Too many attempts. Please wait a few minutes and try again.'
              : (json.error ? String(json.error) : 'Your review could not be submitted. Please try again.');
            this.setStatus(msg, 'error');
          }
        } catch (_) {
          this.setStatus('Connection issue. Please try again in a moment.', 'error');
        } finally {
          btn.disabled = false;
        }
      }
    });
  }
})();